import { LitElement, html, css, PropertyValues, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { keyed } from "lit/directives/keyed.js";
import { resolveRadius } from "../helper/utils/size-resolver";
import { resolveColor } from "../helper/utils/color-resolver";
import { styleMap } from "lit/directives/style-map.js";
import { classMap } from "lit/directives/class-map.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyAvatarFit = "cover" | "contain";

const SIZE_PRESETS: Record<string, string> = {
  sm: "var(--sky-space-8, 2rem)",
  md: "var(--sky-space-10, 2.5rem)",
  lg: "var(--sky-space-12, 3rem)",
};

const SIZE_PX: Record<string, number> = { sm: 32, md: 40, lg: 48 };


/**
 * @element sky-avatar
 *
 * @summary Avatar that renders image content or fallback slot content with configurable size, radius, outline, and loading state.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/avatar
 *
 * @slot - Fallback content (e.g., initials or an icon) rendered when `image` is empty.
 *
 * @property {string} size - Avatar size: preset `sm` | `md` | `lg` or CSS length (for example `50px`).
 * @property {string} image - Image URL; fallback slot is shown on load error.
 * @property {SkyAvatarFit} fit - Image fill mode: `cover` (default) or `contain`.
 * @property {string} radius - Border radius token or CSS value.
 * @property {string} color - Token or CSS color applied to fallback slot content.
 * @property {string} alt - Accessible name when image content conveys meaning.
 * @property {string} outline - CSS `outline` value.
 * @property {string} outlineOffset - CSS `outline-offset` value.
 * @property {boolean} loading - Shows loading overlay when true.
 * @property {boolean} disabled - Dims and disables pointer interactions.
 * @property {boolean} interactive - Enables interactive behavior with keyboard/click activation.
 * @property {boolean} shadow - When true, applies solid surface, border ring, and elevation; no backdrop blur.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @csspart avatar - The avatar container element.
 * @csspart loading-overlay - The loading overlay container (shown when `loading` is true).
 * @csspart spinner - The spinner element shown when loading.
 *
 * @Behavior
 * - Applies image content when available; otherwise renders fallback slot content.
 * - Uses keyed rendering to reset avatar content when image availability changes.
 * - Scales spinner dimensions from resolved avatar size for numeric values.
 * - Respects reduced-motion preference by disabling spinner animation.
 *
 * @example
 * ```html
 * <sky-avatar size="56px" radius="full" color="primary">
 *   JD
 * </sky-avatar>
 * ```
 * ```vue
 * <template>
 *   <sky-avatar size="56px" radius="full" color="primary">
 *     JD
 *   </sky-avatar>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-avatar size="56px" radius="full" color="primary">
 *       JD
 *     </sky-avatar>
 *   );
 * }
 * ```
 */
@customElement("sky-avatar")
export class SkyAvatar extends LitElement {

  /** Avatar size: preset sm|md|lg or CSS length. @default "50px" */
  @property({ type: String }) size: string = "50px";

  /** Image URL. On error, slot is shown. @default "" */
  @property({ type: String }) image: string = "";

  /** How the image fills the avatar. @default "cover" */
  @property({ type: String, reflect: true }) fit: SkyAvatarFit = "cover";

  /** Border radius token or CSS value. @default "md" */
  @property({ type: String }) radius: string = "md";

  /** Accessible name when image is set. @default "" */
  @property({ type: String }) alt: string = "";

  /** Slot text/icon color token/CSS color. @default "dark" */
  @property({ type: String }) color: string = "dark";

  /** Outline string. @default "" */
  @property({ type: String }) outline: string = "";

  /** Outline offset value. @default "2px" */
  @property({ type: String }) outlineOffset: string = "2px";

  /** Loading state. @default false */
  @property({ type: Boolean, reflect: true }) loading: boolean = false;

  /** Disables the avatar. @default false */
  @property({ type: Boolean, reflect: true }) disabled: boolean = false;

  /** When true, focusable with focus ring and click. @default false */
  @property({ type: Boolean }) interactive: boolean = false;

  /**
   * Elevated ring styling (`border`, inset shadow). No backdrop blur on avatar media.
   * `false` (default) keeps the avatar minimal for inline use.
   */
  @property({ type: Boolean, reflect: true })
  shadow = false;

  /** True when image failed to load; show slot instead. */
  @state() private _imageError = false;



  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: inline-flex;
      line-height: 0;
      justify-content: center;
      align-items: center;
    }

    :host([disabled]) {
      opacity: var(--sky-opacity-disabled, 0.5);
      pointer-events: none;
    }

    .avatar {
      width: var(--avatar-size);
      height: var(--avatar-size);
      position: relative;
      background-size: var(--avatar-bg-size, cover);
      background-position: center;
      background-repeat: no-repeat;
      border-radius: var(--avatar-border-radius, var(--sky-radius-secondary));
      display: flex;
      color: var(--slot-text-color);
      justify-content: center;
      align-items: center;
      background-clip: padding-box;
      -webkit-background-clip: padding-box;
      box-sizing: border-box;
      overflow: hidden;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    :host([shadow]) .avatar {
      background-color: var(--sky-glass-solid);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-primary);
      border: var(--sky-border-primary);
    }

    :host([loading]) .avatar {
      will-change: transform;
      transform: translateZ(0);
    }

    .avatar:focus-visible {
      outline: none;
      box-shadow: var(--sky-focus-ring);
    }

    :host([shadow]) .avatar:focus-visible {
      box-shadow:
        var(--sky-focus-ring),
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-primary);
    }

    .avatar--interactive {
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .image-probe {
      position: absolute;
      width: 0;
      height: 0;
      opacity: 0;
      pointer-events: none;
    }

    ::slotted(*) {
      font-family: var(--sky-font);
      font-weight: var(--sky-font-weight-semibold, 600);
      font-size: calc(var(--avatar-size) * 0.38);
      line-height: 1;
      letter-spacing: var(--sky-letter-spacing-normal, 0);
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      object-fit: contain;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .loading-overlay {
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: var(--sky-overlay-secondary);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1;
      visibility: hidden;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    :host([loading]) .loading-overlay {
      visibility: visible;
    }

    .spinner {
      border-width: 2px;
      border-style: solid;
      border-color: var(--sky-border-light);
      border-top-color: var(--slot-text-color, var(--sky-text-primary));
      border-radius: var(--sky-radius-full);
      width: 40px;
      height: 40px;
      animation: spin var(--sky-duration-slow, 1s) linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      :host([loading]) .avatar {
        will-change: auto;
        transform: none;
      }

      .spinner {
        animation: none;
        border-top-color: var(--slot-text-color, var(--sky-text-primary));
        opacity: var(--sky-opacity-muted, 0.7);
      }
    }
  `;

  override updated(changed: PropertyValues): void {
    super.updated(changed);

    if (changed.has("image")) {
      this._imageError = false;
    }

    if (
      changed.has("size") ||
      changed.has("fit") ||
      changed.has("radius") ||
      changed.has("color")
    ) {
      const resolvedSize = SIZE_PRESETS[this.size] ?? this.size;
      this.style.setProperty("--avatar-size", resolvedSize);

      this.style.setProperty(
        "--avatar-bg-size",
        this.fit === "contain" ? "contain" : "cover",
      );

      this.style.setProperty(
        "--avatar-border-radius",
        resolveRadius(this.radius),
      );

      if (this.color) {
        this.style.setProperty("--slot-text-color", resolveColor(this.color));
      }
    }

    if (changed.has("size") || changed.has("loading")) {
      this.adjustSpinnerSize();
    }
  }

  /** Marks image load success and keeps image rendering active. */
  private _onImageLoad = (): void => {
    this._imageError = false;
  };

  /** Marks image load failure and enables fallback slot rendering. */
  private _onImageError = (): void => {
    this._imageError = true;
  };

  /** Re-dispatches click in interactive mode for host-level consumers. */
  private _handleInteractiveClick(e: Event): void {
    if (!this.interactive || this.disabled) return;
    e.stopPropagation();
    this.dispatchEvent(new Event("click", { bubbles: true, composed: true }));
  }

  /** Handles keyboard activation in interactive mode. */
  private _handleInteractiveKeydown(e: KeyboardEvent): void {
    if (!this.interactive || this.disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.dispatchEvent(new Event("click", { bubbles: true, composed: true }));
    }
  }



  /** Derives spinner dimensions from current avatar size. */
  private adjustSpinnerSize(): void {
    const avatarSizePx =
      SIZE_PX[this.size] ?? parseFloat(this.size);
    if (Number.isFinite(avatarSizePx)) {
      const spinnerSize = avatarSizePx * 0.25;
      const spinner = this.shadowRoot?.querySelector<HTMLElement>(".spinner");
      if (spinner) {
        spinner.style.width = `${spinnerSize}px`;
        spinner.style.height = `${spinnerSize}px`;
        spinner.style.borderWidth = `${Math.max(1, spinnerSize / 10)}px`;
      }
    }
  }

  override render() {
    const showImage = this.image && !this._imageError;
    const avatarStyle: Record<string, string> = {};
    if (showImage) {
      avatarStyle["backgroundImage"] = `url(${encodeURI(this.image)})`;
    }
    if (this.outline) {
      avatarStyle["outline"] = this.outline;
    }
    if (this.outlineOffset) {
      avatarStyle["outlineOffset"] = this.outlineOffset;
    }

    const role = this.interactive
      ? "button"
      : showImage && this.alt
        ? "img"
        : nothing;
    const ariaLabel =
      this.interactive && this.alt
        ? this.alt
        : showImage && this.alt
          ? this.alt
          : nothing;

    return html`
      <div
        class=${classMap({
          avatar: true,
          "avatar--interactive": !!this.interactive,
        })}
        part="avatar"
        style=${styleMap(avatarStyle)}
        role=${role}
        aria-label=${ariaLabel}
        tabindex=${this.interactive && !this.disabled ? "0" : nothing}
        @click=${this._handleInteractiveClick}
        @keydown=${this._handleInteractiveKeydown}
      >
        ${this.image
          ? html`<img
              class="image-probe"
              src=${this.image}
              alt=""
              @load=${this._onImageLoad}
              @error=${this._onImageError}
            />`
          : nothing}
        <div class="loading-overlay" part="loading-overlay">
          <div class="spinner" part="spinner"></div>
        </div>
        ${keyed(
          showImage ? "_img_" : "_slot_",
          when(!showImage, () => html`<slot></slot>`, () => nothing),
        )}
      </div>
    `;
  }
}
